import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Globe, Zap, CheckCircle2, BarChart3 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { EyeCaptainLogo } from "@/components/brand/EyeCaptainLogo";

interface FreeCroAnalysisPopupProps {
  onClose: () => void;
}

const CRO_OPTIONS = [
  {
    el: {
      headline: "\u039a\u03ac\u03c4\u03b9 \u03b4\u03b5\u03bd \u03c0\u03ac\u03b5\u03b9 \u03ba\u03b1\u03bb\u03ac...",
      title: "\u03a4\u03bf website \u03c3\u03bf\u03c5 \u03c7\u03ac\u03bd\u03b5\u03b9 \u03c0\u03b5\u03bb\u03ac\u03c4\u03b5\u03c2.",
      subtitle: "\u039f\u03b9 \u03b5\u03c0\u03b9\u03c3\u03ba\u03ad\u03c0\u03c4\u03b5\u03c2 \u03c6\u03b5\u03cd\u03b3\u03bf\u03c5\u03bd \u03c7\u03c9\u03c1\u03af\u03c2 \u03bd\u03b1 \u03b1\u03b3\u03bf\u03c1\u03ac\u03c3\u03bf\u03c5\u03bd. \u03a4\u03bf AI \u03b8\u03b1 \u03c3\u03bf\u03c5 \u03c0\u03b5\u03b9 \u03b1\u03ba\u03c1\u03b9\u03b2\u03ce\u03c2 \u03c4\u03b9 \u03c4\u03bf\u03c5\u03c2 \u03c3\u03c4\u03b1\u03bc\u03b1\u03c4\u03ac\u03b5\u03b9.",
    },
    en: {
      headline: "Something's off...",
      title: "Your website is losing customers.",
      subtitle: "Visitors leave without buying. Our AI will show you exactly what's stopping them.",
    },
  },
  {
    el: {
      headline: "\u03a4\u03b1 \u03c7\u03c1\u03ae\u03bc\u03b1\u03c4\u03b1 \u03c5\u03c0\u03ac\u03c1\u03c7\u03bf\u03c5\u03bd",
      title: "\u03a0\u03cc\u03c3\u03b1 \u03c7\u03ac\u03bd\u03b5\u03b9\u03c2 \u03b1\u03c0\u03cc \u03ba\u03b1\u03ba\u03cc UX;",
      subtitle: "\u039a\u03ac\u03b8\u03b5 \u03bc\u03ad\u03c1\u03b1 \u03c0\u03bf\u03c5 \u03c4\u03bf site \u03c3\u03bf\u03c5 \u03ad\u03c7\u03b5\u03b9 \u03c0\u03c1\u03bf\u03b2\u03bb\u03ae\u03bc\u03b1\u03c4\u03b1, \u03c7\u03ac\u03bd\u03b5\u03b9\u03c2 \u03bb\u03b5\u03c6\u03c4\u03ac. \u0394\u03b5\u03c2 \u03b1\u03ba\u03c1\u03b9\u03b2\u03ce\u03c2 \u03c0\u03bf\u03cd \u03c0\u03b7\u03b3\u03b1\u03af\u03bd\u03bf\u03c5\u03bd.",
    },
    en: {
      headline: "The money is there",
      title: "How much are you losing to bad UX?",
      subtitle: "Every day your site has problems, you're losing real money. See exactly where it's going.",
    },
  },
];

const getRandomOption = () => CRO_OPTIONS[Math.floor(Math.random() * CRO_OPTIONS.length)];

export function FreeCroAnalysisPopup({ onClose }: FreeCroAnalysisPopupProps) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [option] = useState(getRandomOption);
  const dynamicCopy = option[language] || option.en;

  const t = {
    el: {
      features: [
        "UX & Conversion \u03c0\u03c1\u03bf\u03b2\u03bb\u03ae\u03bc\u03b1\u03c4\u03b1",
        "Prioritized \u03bb\u03cd\u03c3\u03b5\u03b9\u03c2",
        "\u0391\u03bd\u03ac\u03bb\u03c5\u03c3\u03b7 \u03b1\u03bd\u03c4\u03b1\u03b3\u03c9\u03bd\u03b9\u03c3\u03c4\u03ce\u03bd"
      ],
      placeholder: "\u03a4\u03bf domain \u03c3\u03bf\u03c5 (\u03c0.\u03c7. example.com)",
      cta: "\u0394\u03b5\u03af\u03be\u03b5 \u03bc\u03bf\u03c5 \u03c4\u03b1 \u03c0\u03c1\u03bf\u03b2\u03bb\u03ae\u03bc\u03b1\u03c4\u03b1",
      badge: "100% \u0394\u03c9\u03a1\u0395\u0391\u039d",
      noThanks: "\u0398\u03b1 \u03c3\u03c5\u03bd\u03b5\u03c7\u03af\u03c3\u03c9 \u03bd\u03b1 \u03c7\u03ac\u03bd\u03c9 \u03c0\u03b5\u03bb\u03ac\u03c4\u03b5\u03c2...",
    },
    en: {
      features: [
        "UX & Conversion problems",
        "Prioritized solutions",
        "Competitor audit"
      ],
      placeholder: "Your domain (e.g. example.com)",
      cta: "Show me the problems",
      badge: "100% FREE",
      noThanks: "I'll keep losing customers...",
    },
  };

  const text = t[language] || t.en;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setLoading(true);
    let cleanDomain = domain.trim();
    if (!cleanDomain.startsWith('http://') && !cleanDomain.startsWith('https://')) {
      cleanDomain = 'https://' + cleanDomain;
    }
    onClose();
    navigate(`/free-audit?url=${encodeURIComponent(cleanDomain)}`);
  };

  return (
    <div className="relative p-6 md:p-8">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col items-center text-center">
        <EyeCaptainLogo
          alt="EyeCaptain"
          className="h-8 w-auto mb-4 opacity-80"
        />

        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium mb-3">
          <BarChart3 className="h-3 w-3" />
          {text.badge}
        </span>

        <h2 className="text-xl font-bold mb-1">
          {dynamicCopy.headline}
        </h2>
        <p className="text-base font-semibold text-primary mb-2">
          {dynamicCopy.title}
        </p>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed max-w-xs mx-auto">
          {dynamicCopy.subtitle}
        </p>

        <div className="space-y-1.5 mb-5 w-full max-w-xs mx-auto text-left">
          {text.features.map((feature, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="w-full max-w-xs mx-auto space-y-2.5">
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={text.placeholder}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="pl-9 h-10 text-sm bg-muted/30 border-muted-foreground/20 focus:border-primary rounded-lg"
              required
              disabled={loading}
            />
          </div>
          <Button
            type="submit"
            size="lg"
            className="w-full h-11 text-sm font-semibold rounded-lg"
            disabled={loading}
          >
            {loading ? (
              <span className="animate-spin">
                <Zap className="h-4 w-4" />
              </span>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-2" />
                {text.cta}
              </>
            )}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={onClose}
            className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors italic"
          >
            {text.noThanks}
          </button>
        </div>
      </div>
    </div>
  );
}
